import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import VitalsMonitorForm from "../VitalsMonitorChart/VitalsMonitorForm";

import { useDispatch, useSelector } from "react-redux";
import { createVitalsChart } from "../../../store/slices/vitalsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddVitalsMonitor = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isLoading } = useSelector(state => state.vitals);
    const { state } = useLocation();
    const { caseId, patientId } = state || {};


    const [formData, setFormData] = useState({
        patientId, // initialize
        caseId,    // initialize
        name: "",
        dob: "",
        documentName: "Vitals Monitoring Chart",
        effectiveDate: "",
        approvedBy: "",
        approvedDate: "",
        nextRevisionBy: "",
        nextRevisionDate: "",
        vitalsRecords: [],
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                            dob: patient.dob ? patient.dob.split('T')[0] : "",
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient details:", err));
        }
    }, [dispatch, patientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required.";
        if (!formData.dob) newErrors.dob = "DOB is required.";
        if (formData.vitalsRecords.length === 0) newErrors.vitalsRecords = "At least one vital record is required.";
        if (formData.vitalsRecords.length === 0) {
            newErrors.vitalsRecords = "At least one vital record is required.";
        } else {
            formData.vitalsRecords.forEach((r, i) => {
                if (!r.dateTime) {
                    newErrors[`vitalRecord_${i}_dateTime`] = `Date & Time is required for row ${i + 1}`;
                }
            });
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields before submitting.",
                icon: "warning",
            });
            return;
        }

        try {
            // Map camelCase to snake_case for API
            const payload = {
                patient_id: formData.patientId || null,
                case_id: formData.caseId || null,
                name: formData.name,
                dob: formData.dob,
                document_name: formData.documentName,
                effective_date: formData.effectiveDate,
                approved_by: formData.approvedBy,
                approved_date: formData.approvedDate,
                next_revision_by: formData.nextRevisionBy,
                next_revision_date: formData.nextRevisionDate,
                vital_records: formData.vitalsRecords.map((r) => ({
                    date_time: r.dateTime,
                    bp: r.bp,
                    pulse: r.pulse,
                    resp: r.resp,
                    temp: r.temp,
                    rbs: r.rbs,
                    fbs: r.fbs,
                    weight: r.weight,
                    remarks: r.remarks,
                    nurse: r.nurse,
                })),
            };
            await dispatch(createVitalsChart(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Vitals Monitoring Chart added successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/cases/${payload.case_id}/profile`, { state: { patientId: payload.patient_id, caseId: payload.case_id } });
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: err || "Something went wrong while saving the chart.",
                icon: "error",
            });
        }
    };


    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card">
                    <div className="card-body">
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors)
                                    .filter(Boolean)
                                    .map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <VitalsMonitorForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-2-line me-1"></i> Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddVitalsMonitor;
